import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PriceCalculator from '../home/components/PriceCalculator';

export default function TaxiUtrechtSchipholPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Taxi Utrecht Schiphol | 24/7 Airport Taxi & Vaste Prijs';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Taxi van Utrecht naar Schiphol Airport nodig? 24/7 beschikbaar, vaste prijs mogelijk en comfortabel vervoer. Direct reserveren.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'taxi utrecht schiphol, schiphol taxi utrecht, airport taxi utrecht, taxi naar schiphol');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://www.utrechtairporttaxi.nl/taxi-utrecht-schiphol');
    }

    // Add FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const faqs = [
    {
      question: 'Wat kost een taxi naar Schiphol?',
      answer: 'Voor veel ritten is een vaste prijs mogelijk. Neem contact op voor een prijs.'
    },
    {
      question: 'Rijden jullie \'s nachts?',
      answer: 'Ja, wij rijden 24/7.'
    },
    {
      question: 'Helpen chauffeurs met bagage?',
      answer: 'Ja, altijd.'
    },
    {
      question: 'Hoe lang duurt de rit Utrecht – Schiphol?',
      answer: 'Dit hangt af van verkeer. Gemiddeld 30-45 minuten. Wij plannen altijd ruim zodat je op tijd aankomt.'
    },
    {
      question: 'Kunnen jullie mij ophalen op Schiphol?',
      answer: 'Ja, geef je vluchtnummer door en wij volgen je aankomst. We houden rekening met eventuele vertragingen.'
    },
    {
      question: 'Zijn er extra kosten voor nachtelijke ritten?',
      answer: 'Bij een vaste prijs zijn alle kosten inbegrepen, ook \'s nachts. Vraag bij reservering naar de mogelijkheden.'
    },
    {
      question: 'Kan ik zakelijk reizen en een factuur ontvangen?',
      answer: 'Ja, wij verzorgen zakelijk vervoer en sturen altijd een factuur voor administratie.'
    },
    {
      question: 'Hoeveel bagage kan er mee?',
      answer: 'Standaard passen 3-4 koffers. Meer bagage? Geef dit bij reservering door, dan regelen wij een grotere auto.'
    }
  ];

  const scrollToBooking = () => {
    const bookingSection = document.getElementById('calculator');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/d7e93375864d896bf8b57ee57be20554.jpg')] bg-cover bg-center"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Taxi Utrecht – Schiphol Airport
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
              Betrouwbare airport taxi van Utrecht naar Schiphol. 24/7 service, vaste prijs mogelijk en direct reserveren via WhatsApp of telefoon.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={scrollToBooking}
                className="px-6 sm:px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line mr-2"></i>Reserveer Nu & Zie Prijs
              </button>
              <a href="tel:+31658824429" className="px-6 sm:px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>Bel Direct
              </a>
              <a href="https://wa.me/31658824429" className="px-6 sm:px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>WhatsApp Reserveren
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <div id="calculator">
        <PriceCalculator />
      </div>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Taxi Utrecht naar Schiphol Airport</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Een <strong>taxi van Utrecht naar Schiphol Airport</strong> is de meest comfortabele en betrouwbare manier om te reizen. Geen gedoe met parkeren, overstappen of vertragingen. Met Utrecht Airport Taxi word je opgehaald op het afgesproken tijdstip en rechtstreeks naar de juiste vertrekhal gebracht.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Onze chauffeurs rijden dagelijks deze route en houden rekening met verkeer, inchecktijden en vertrekmomenten. Voor veel ritten kunnen wij vooraf een <strong>vaste prijs</strong> afspreken, zodat je precies weet waar je aan toe bent.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ook <strong>ophalen vanaf Schiphol naar Utrecht</strong> is mogelijk. Geef je vluchtnummer door en wij houden rekening met eventuele vertragingen.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 my-8">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Voordelen van Onze Service</h3>
              <ul className="space-y-2 mb-0">
                <li className="text-slate-700"><strong>24/7 beschikbaar</strong></li>
                <li className="text-slate-700"><strong>Vaste prijs mogelijk</strong></li>
                <li className="text-slate-700"><strong>Comfortabele voertuigen met bagageruimte</strong></li>
                <li className="text-slate-700"><strong>Altijd op tijd door ruime planning</strong></li>
                <li className="text-slate-700"><strong>Zakelijk en particulier vervoer</strong></li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Hoe Werkt Reserveren?</h2>
            
            <div className="grid md:grid-cols-3 gap-6 my-8">
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">1</div>
                <h3 className="font-bold text-slate-900 mb-2">Geef je ritgegevens door</h3>
                <p className="text-sm text-slate-600">Bel, WhatsApp of gebruik het formulier</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">2</div>
                <h3 className="font-bold text-slate-900 mb-2">Ontvang bevestiging en vaste prijs</h3>
                <p className="text-sm text-slate-600">Direct duidelijkheid over de kosten</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">3</div>
                <h3 className="font-bold text-slate-900 mb-2">Chauffeur haalt je op</h3>
                <p className="text-sm text-slate-600">En brengt je veilig naar Schiphol</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Reistijd en Planning</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              De <strong>reistijd van Utrecht naar Schiphol</strong> varieert tussen 30 en 45 minuten, afhankelijk van het tijdstip en de verkeersdrukte. Onze chauffeurs kennen de beste routes en monitoren het verkeer continu via navigatiesystemen.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Voor vroege vluchten of belangrijke afspraken plannen wij altijd extra tijd in. Zo kom je gegarandeerd op tijd aan bij de juiste terminal. Bij drukte op de A2 of A12 kiezen we automatisch een alternatieve route.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Ophaalservice vanaf Schiphol</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ook <strong>ophalen vanaf Schiphol naar Utrecht</strong> behoort tot onze diensten. Geef bij reservering je vluchtnummer door, dan volgen wij je aankomst automatisch. Bij vertraging hoef je niets te doen – wij passen de ophaaltijd aan.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Na landing heb je ongeveer 30-45 minuten nodig voor bagage en douane. Onze chauffeur wacht op de afgesproken plek in de aankomsthal en helpt met je koffers naar de auto.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Zakelijk Vervoer naar Schiphol</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Veel zakelijke reizigers kiezen voor onze <strong>taxi service naar Schiphol</strong> vanwege de betrouwbaarheid en het comfort. Je kunt onderweg werken, bellen of ontspannen. Wij zorgen voor een rustige rit zonder stress.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Voor bedrijven bieden wij facturen en vaste afspraken. Regelmatig naar Schiphol? Vraag naar onze zakelijke tarieven en voorkeursbehandeling.
            </p>

            <div className="bg-slate-50 rounded-2xl p-8 my-12 border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Waarom Kiezen voor Onze Taxi Service?</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-time-line mr-2 text-amber-500"></i>
                    Altijd op Tijd
                  </h4>
                  <p className="text-slate-700">Ruime planning en verkeersbewaking zorgen dat je nooit je vlucht mist.</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-shield-check-line mr-2 text-amber-500"></i>
                    Vaste Prijs Garantie
                  </h4>
                  <p className="text-slate-700">Geen verrassingen achteraf. De afgesproken prijs is wat je betaalt.</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-car-line mr-2 text-amber-500"></i>
                    Comfortabele Auto's
                  </h4>
                  <p className="text-slate-700">Schone, goed onderhouden voertuigen met voldoende bagageruimte.</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-customer-service-2-line mr-2 text-amber-500"></i>
                    Professionele Chauffeurs
                  </h4>
                  <p className="text-slate-700">Ervaren, vriendelijke chauffeurs die de route perfect kennen.</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Lokale Service vanuit Utrecht</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ophalen in heel Utrecht: <strong>Leidsche Rijn, Centrum, Overvecht, Lombok, Kanaleneiland, Hoograven</strong> en omgeving.
            </p>

            <div className="bg-slate-50 rounded-2xl p-8 my-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Direct Reserveren?</h3>
              <p className="text-lg text-slate-700 mb-6">
                Neem contact op voor een vaste prijs of boek direct via WhatsApp.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={scrollToBooking}
                  className="px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line mr-2"></i>Reserveer Nu
                </button>
                <a href="tel:+31658824429" className="px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>Bel Direct
                </a>
                <a href="https://wa.me/31658824429" className="px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                  <i className="ri-whatsapp-line mr-2"></i>WhatsApp
                </a>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Andere Airport Taxi Routes</h2>
            
            <div className="grid md:grid-cols-3 gap-4 my-8">
              <Link to="/" className="block p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                  <i className="ri-home-line mr-2 text-amber-500"></i>
                  Airport Taxi Utrecht
                </h4>
                <p className="text-sm text-slate-600">Terug naar de hoofdpagina</p>
              </Link>

              <Link to="/taxi-utrecht-eindhoven-airport" className="block p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                  <i className="ri-plane-line mr-2 text-amber-500"></i>
                  Taxi Utrecht – Eindhoven Airport
                </h4>
                <p className="text-sm text-slate-600">Comfortabel naar Eindhoven Airport</p>
              </Link>

              <Link to="/taxi-utrecht-rotterdam-airport" className="block p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                  <i className="ri-plane-line mr-2 text-amber-500"></i>
                  Taxi Utrecht – Rotterdam Airport
                </h4>
                <p className="text-sm text-slate-600">Direct naar Rotterdam The Hague Airport</p>
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 text-center">Veelgestelde Vragen</h2>
          <p className="text-xl text-slate-600 text-center mb-12">Alles wat u moet weten over onze taxi service naar Schiphol</p>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <summary className="text-lg font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                  <span>{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-2xl text-amber-500"></i>
                </summary>
                <p className="mt-4 text-slate-700 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
